import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { conversationAPI } from '../api';
import PasswordDialog from '../components/PasswordDialog';

const ConversationsContext = createContext();

export const useConversations = () => {
  const context = useContext(ConversationsContext);
  if (!context) {
    throw new Error('useConversations must be used within a ConversationsProvider');
  }
  return context;
};

export const ConversationsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState([]); // [{ _id, title, updatedAt }]
  const [currentId, setCurrentId] = useState(null);
  // Bumped whenever the user picks a conversation or starts a new one, so the
  // Chat component knows to (re)load — even if it navigates to the same id.
  const [navSeq, setNavSeq] = useState(0);
  const [loading, setLoading] = useState(false);
  // Delete-password dialog (rendered by this provider, so every caller of
  // deleteConversation gets it without wiring anything up themselves)
  const [pwOpen, setPwOpen] = useState(false);
  const [pwError, setPwError] = useState('');
  const pwResolver = useRef(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const list = await conversationAPI.list();
      setConversations(list || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setConversations([]);
      setCurrentId(null);
    }
  }, [isAuthenticated, refresh]);

  // Start a fresh, empty chat (no conversation created until first message)
  const newChat = useCallback(() => {
    setCurrentId(null);
    setNavSeq(s => s + 1);
  }, []);

  // Open an existing conversation by id
  const openConversation = useCallback((id) => {
    setCurrentId(id);
    setNavSeq(s => s + 1);
  }, []);

  // Called by Chat when the first message of a new chat is sent
  const createConversation = useCallback(async (title, messages) => {
    const convo = await conversationAPI.create(title, messages);
    setConversations(prev => [{ _id: convo._id, title: convo.title, updatedAt: convo.updatedAt }, ...prev]);
    setCurrentId(convo._id);
    return convo._id;
  }, []);

  // Persist the current message list to an existing conversation
  const saveConversation = useCallback(async (id, messages) => {
    try {
      await conversationAPI.save(id, messages);
      // Bump this conversation to the top of the list
      setConversations(prev => {
        const idx = prev.findIndex(c => c._id === id);
        if (idx === -1) return prev;
        const item = { ...prev[idx], updatedAt: new Date().toISOString() };
        const rest = prev.filter(c => c._id !== id);
        return [item, ...rest];
      });
    } catch (error) {
      console.error('Failed to save conversation:', error);
    }
  }, []);

  const renameConversation = useCallback(async (id, title) => {
    const updated = await conversationAPI.rename(id, title);
    setConversations(prev => prev.map(c => (c._id === id ? { ...c, title: updated.title } : c)));
  }, []);

  // Ask for the delete password through the in-app dialog. Returns a promise
  // that resolves with what the user typed, or null if they cancelled — so
  // deleteConversation below can stay a straightforward async loop.
  const askPassword = useCallback((errorText) => {
    return new Promise(resolve => {
      pwResolver.current = resolve;
      setPwError(errorText || '');
      setPwOpen(true);
    });
  }, []);

  const closePasswordDialog = useCallback((value) => {
    setPwOpen(false);
    setPwError('');
    const resolve = pwResolver.current;
    pwResolver.current = null;
    if (resolve) resolve(value);
  }, []);

  // Delete may be password-protected (admin sets it per user). The server is
  // the authority: we try, and if it answers DELETE_PASSWORD_REQUIRED we ask
  // for the password and retry. Wrong password → the dialog reopens with the
  // error shown inline.
  const deleteConversation = useCallback(async (id) => {
    let password;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await conversationAPI.remove(id, password);
        setConversations(prev => prev.filter(c => c._id !== id));
        if (currentId === id) {
          newChat();
        }
        return;
      } catch (err) {
        const code = err.response?.data?.code;
        if (code === 'DELETE_PASSWORD_REQUIRED' || code === 'DELETE_PASSWORD_WRONG') {
          password = await askPassword(
            code === 'DELETE_PASSWORD_WRONG' ? 'Galat password. Dobara try karo.' : ''
          );
          if (!password) return; // cancelled
          continue;
        }
        throw err;
      }
    }
    // Three wrong tries — give up quietly; nothing was deleted
  }, [currentId, newChat, askPassword]);

  const value = {
    conversations,
    currentId,
    navSeq,
    loading,
    refresh,
    newChat,
    openConversation,
    createConversation,
    saveConversation,
    renameConversation,
    deleteConversation
  };

  return (
    <ConversationsContext.Provider value={value}>
      {children}
      <PasswordDialog
        open={pwOpen}
        title="Password required"
        message="Ye chat delete karne ke liye password chahiye. Admin se lo."
        error={pwError}
        confirmLabel="Delete chat"
        onConfirm={closePasswordDialog}
        onCancel={() => closePasswordDialog(null)}
      />
    </ConversationsContext.Provider>
  );
};

export default ConversationsContext;
